import { useEffect, useState } from "react";
import { LoaderFunctionArgs, redirect, useLoaderData } from "react-router-dom";

import { queries } from "@oneleet/api_client";

import { ArrowRight } from "lucide-react";

import queryClient from "../query_client";
import { Button } from "@oneleet/components";

type RedirectError = {
  title: string;
  message: string;
};

async function getSelectedTenantMembership() {
  const memberships = await queryClient.fetchQuery(queries.membership.list);
  return memberships[0];
}

async function redirectToTenantPage(subPath: string) {
  const selectedMembership = await getSelectedTenantMembership();
  return redirect("/tenants/" + selectedMembership.tenantId + subPath);
}

async function redirectToIntegrationPage(subPath: string) {
  const selectedMembership = await getSelectedTenantMembership();

  switch (subPath) {
    case "new":
      return redirectToTenantPage("/integrations/new");
    case "":
      return redirectToTenantPage("/integrations");
  }

  const { integrations } = await queryClient.fetchQuery(
    queries.integration.list(selectedMembership.tenantId),
  );

  for (const integration of integrations) {
    if (integration.integrationType?.formTag === subPath) {
      return redirectToTenantPage("/integrations/" + integration.id);
    }
  }

  return {
    title: "Integration not found",
    message:
      `Integration '${subPath.toLocaleLowerCase()}' not found in tenant '${selectedMembership.tenant?.name}'.\n` +
      `If you think this is an unintended error please contact us on Slack or by email.`,
  };
}

async function redirectToControlsPage(subPath: string) {
  const selectedMembership = await getSelectedTenantMembership();

  const controls = await queryClient.fetchQuery(
    queries.control.list(selectedMembership.tenantId),
  );

  for (const control of controls) {
    if (control.controlTypeId.toLowerCase() === subPath.toLowerCase()) {
      return redirectToTenantPage("/controls/" + control.id);
    }
  }

  return {
    title: "Control not found",
    message:
      `Control '${subPath.toLocaleLowerCase()}' not found in tenant '${selectedMembership.tenant?.name}'.\n` +
      `If you think this is an unintended error please contact us on Slack or by email.`,
  };
}

async function redirectToMonitorsPage(subPath: string) {
  const selectedMembership = await getSelectedTenantMembership();

  const { integrations } = await queryClient.fetchQuery(
    queries.integration.list(selectedMembership.tenantId),
  );

  for (const integration of integrations) {
    const monitors = await queryClient.fetchQuery(
      queries.monitor.listByIntegration(integration.id),
    );
    for (const monitor of monitors) {
      if (monitor.monitorType.id.toLowerCase() === subPath.toLowerCase()) {
        return redirectToTenantPage("/monitors/" + monitor.id);
      }
    }
  }

  return {
    title: "Monitor not found",
    message:
      `Monitor '${subPath.toLocaleLowerCase()}' not found in tenant '${selectedMembership.tenant?.name}'.\n` +
      `If you think this is an unintended error please contact us on Slack or by email.`,
  };
}

const shortLinkRedirector = async (currentUrl: string) => {
  const url = new URL(currentUrl);

  switch (url.pathname) {
    case "/integrations/new":
      return await redirectToTenantPage("/integrations/new");
    case "/settings":
      return await redirectToTenantPage("/settings");
    case "/policies":
      return await redirectToTenantPage("/policies");
    case "/evidence":
      return await redirectToTenantPage("/evidence");
    case "/risk-management":
      return await redirectToTenantPage("/risk-management");
    case "/trust-center":
      return await redirectToTenantPage("/trust-center");
    case "/security-training":
      return await redirectToTenantPage("/security-training");
    case "/attack-surface":
      return await redirectToTenantPage("/attack-surface");
    case "/code-security":
      return await redirectToTenantPage("/code-security");
    case "/penetration-tests":
      return await redirectToTenantPage("/penetration-tests");
    case "/access-reviews":
      return await redirectToTenantPage("/access-reviews");
    case "/people":
      return await redirectToTenantPage("/people");
    case "/people/onboarding":
      return await redirectToTenantPage("/people?tab=onboarding");
    case "/people/offboarding":
      return await redirectToTenantPage("/people?tab=offboarding");
    case "/mdm":
      return await redirectToTenantPage("/devices");
    case "/mdm-setup":
      return await redirectToTenantPage("/devices?tab=mdmSetup");
    case "/mdm-configuration":
      return await redirectToTenantPage("/devices?tab=mdmConfiguration");
    case "/vendors":
      return await redirectToTenantPage("/vendors");
    case "/domains":
      return await redirectToTenantPage("/domains");
  }

  if (url.pathname.startsWith("/integrations")) {
    // TODO: check permissions if user is allowed to access integrations
    return await redirectToIntegrationPage(
      url.pathname.replace("/integrations/", ""),
    );
  }

  if (url.pathname.startsWith("/controls")) {
    // TODO: check permissions if user is allowed to access controls
    return await redirectToControlsPage(url.pathname.replace("/controls/", ""));
  }

  if (url.pathname.startsWith("/monitors")) {
    // TODO: check permissions if user is allowed to access monitors
    return await redirectToMonitorsPage(url.pathname.replace("/monitors/", ""));
  }

  return null;
};

const uuidRegex =
  "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const frameworksRegex = new RegExp(
  `^/tenants/(${uuidRegex})/frameworks(?:/(${uuidRegex}))?$`,
);

function camelToScreamingSnake(camelCaseString: string): string {
  return camelCaseString.replace(/([A-Z])/g, "_$1").toUpperCase();
}

// eslint-disable-next-line react-refresh/only-export-components
export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const path = url.pathname;
  const searchParams = url.searchParams;

  const match = path.match(frameworksRegex);
  if (match) {
    const tenantId = match[1];
    const frameworkId = match[2];

    if (frameworkId) {
      if (searchParams.get("tab") === "requirements") {
        return redirect(`/tenants/${tenantId}/program?tab=requirements`);
      }
      return redirect(`/tenants/${tenantId}/program`);
    }

    const newParams = new URLSearchParams();
    if (searchParams.size > 0) {
      newParams.set("tab", "controls");
    }

    if (searchParams.has("search")) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      newParams.set("c_search", searchParams.get("search")!);
    }

    if (searchParams.get("status") !== null) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const newStatus = camelToScreamingSnake(searchParams.get("status")!);
      newParams.set("c_status", newStatus);
    }

    if (searchParams.has("assignedTo")) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const assignedTo = searchParams.get("assignedTo")!;
      if (assignedTo.match(uuidRegex)) {
        newParams.set("assignedTo", assignedTo);
      }
    }

    const newUrl = `/tenants/${tenantId}/program?${newParams.toString()}`;
    return redirect(newUrl);
  }

  return await shortLinkRedirector(request.url);
}

export default function Redirect() {
  const data: RedirectError | Response = useLoaderData();

  const [title, setTitle] = useState<string>("404 Not Found");
  const [message, setMessage] = useState<string>(
    "This page was not found, if you think this is an unintended error please contact us on Slack or by email.",
  );

  useEffect(() => {
    if (!(data instanceof Response) && data) {
      setTitle(data.title);
      setMessage(data.message);
    }
  }, [data]);

  return (
    <>
      <div className="fixed top-0 z-50 flex h-16 w-full shrink-0 items-center gap-x-4 bg-[#fafafa] px-4 sm:gap-x-6 sm:px-6 lg:px-8">
        <div className="flex-right z-50 flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
          <div className="flex flex-1 flex-row items-center justify-end gap-x-4 lg:gap-x-6">
            <Button label="Logout" variant="plain" color="gray" />
          </div>
        </div>
      </div>
      <main className="grid h-full place-items-center bg-[#fafafa] px-6 py-24 sm:py-32 lg:px-8">
        <div className="text-center">
          <p className="text-lg font-semibold text-red-600">{"Error"}</p>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            {title}
          </h1>
          <p className="mt-6 whitespace-pre-line text-base leading-7 text-gray-600">
            {message}
          </p>
          <div className="mt-10 flex items-center justify-center gap-x-6">
            <Button
              label="Refresh page"
              variant="solid"
              color="brand"
              size="large"
              onClick={() => window.location.reload()}
            />
            <a href="mailto:support@oneleet.com">
              <Button
                label="Contact us"
                variant="plain"
                color="gray"
                size="large"
                endIcon={<ArrowRight />}
              />
            </a>
          </div>
        </div>
      </main>
    </>
  );
}
